<template>
	<div class="declaration-container">
		<tw-title
			description="Please take a moment to review the important information and eligibility criteria below">Declaration</tw-title>

		<div class="relative" :class="{ 'h-72': loading }">
			<div v-if="!loading">
				<div class="declaration">

					<div class="declaration-text">
						<p class="mb-2">
							We have not provided a personal recommendation regarding the suitability of this insurance
							policy for your specific needs. This policy is designed to meet the demands and needs of
							individuals seeking coverage for specific risks related to their bike, including theft,
							accidental damage, vandalism, bodily injury or damage to third-party property while using the
							bike.
						</p>
						<p>
							Please take a moment to read through the eligibility conditions of cover below:
						</p>
						<ul class="list-decimal my-5 mx-10">
							<li>
								you must be aged 18 or over at the time of purchasing this insurance and have accepted the
								Essential Security and Approved Lock requirements;
							</li>
							<li>
								you must be the owner of the bicycle at the time of buying this insurance and at the time of
								any event leading to a claim;
							</li>
							<li>
								you must be resident in the United Kingdom
							</li>
							<li>
								you must have no unspent criminal convictions (other than motoring offences);
							</li>
							<li>
								you must understand that there is no cover under this policy for any use in connection with
								a profession, business or occupation (other than commuting);
							</li>
							<li>
								there must be no existing accidental damage to the bicycle and accessories at the time you
								purchase the policy that would subsequently lead to a claim; and
							</li>
							<li>
								if you are insuring an electric bike (ebike) it must adhere the relevant UK legislation and
								rules for ‘electrically assisted pedal cycles’ (EAPC’s);
								<a target='blank'
									href="https://www.gov.uk/electric-bike-rules">https://www.gov.uk/electric-bike-rules</a>
							</li>
						</ul>

					</div>
					<div class="mt-4">
						<fieldset>
							<div>
								<div class="relative flex items-start bg-teal-100 px-2 py-4 rounded-md">
									<div class="flex items-center h-5">
										<input id="comments" type="checkbox" v-model="termsAndConditions"
											class="form-checkbox h-6 w-6 text-teal-600 transition duration-150 ease-in-out">
									</div>
									<div class="ml-3 text-sm leading-5">
										<label for="comments" class="font-medium text-gray-700 cursor-pointer">Please tick
											here to confirm
											that you meet the eligibility conditions of the policy and that you have read
											and agree with all the above statements. You also confirm that you have read the
											<a class="cursor-pointer"
												:href="baseUrl + '/download/' + quote.quote_id + '/IPID'">Insurance Product
												Information Document</a>, the <a class="cursor-pointer"
												:href="baseUrl + '/download/' + quote.quote_id + '/Policy_Document'">Policy
												Wording</a>, and the <a class="cursor-pointer" target="blank"
												href="/lock-requirements">Approved Lock Requirements</a> to ensure the
											policy meets your needs.</label>
									</div>
								</div>
							</div>
						</fieldset>
					</div>

					<div class="mt-4">
						<fieldset>
							<div>
								<div
									class="relative flex items-start bg-gray-50 border border-gray-200 px-2 pt-6 pb-3 rounded-md">
									<div class="flex items-center gap-4">
										<i class="far fa-lock-alt text-3xl px-4 text-gray-700"></i>
									</div>
									<div class="ml-3 flex-col flex text-sm leading-5">
										<div class="mb-4">
											<h1 class="font-semibold text-lg">Approved Lock Requirements</h1>
										</div>
										<div class="mb-4">
											<span class="font-medium text-gray-700">
												In accordance with the essential security requirements of the policy, and
												where stipulated, you will need to lock your bike using an approved <span
													class="font-bold">‘Sold
													Secure’ Gold</span> rated lock.
											</span>
										</div>


										<div v-if="bikeLock">
											<span class="mb-4 text-gray-700">
												A <span class="font-bold">Sold Secure</span> approved lock provides a better
												level of security. The higher
												the lock rating the more effective it is at preventing theft. You must have
												a <span class="font-bold">Sold Secure Gold</span> Rated lock or higher.
											</span>
											<div class="py-8 mx-auto flex">
												<div class="rounded-md shadow md:w-2/4 w-full p-4 bg-white">
													<div class="grid grid-cols-2">
														<div class="image m-auto">
															<img src="/images/sold-secure/gold.png" />
														</div>
														<div class="flex text-center flex-col">
															<span class="mb-4 font-bold text-lg">Gold</span>
															<span class="mb-4">All Bikes</span>
															<img src="/images/sold-secure/tick.png" alt="" class="m-auto" />
														</div>
													</div>
												</div>

											</div>

											<span class="mb-4 text-gray-700">
												All approved Sold Secure locks should come with the Sold
												Secure sticker or logo on them. You can also check the <a
													href="https://www.soldsecure.com/approved-product-search"
													target="_blank" class="text-primary">Sold Secure approved lock
													list</a> online to
												make sure you have the correct lock.
											</span>
										</div>

										<div class="py-4">
											<tw-button variant="ghost bg-white" @click="lockChange">
												<div v-if="bikeLock == false">
													What is a Sold Secure Lock <i class="fa fa-chevron-right mx-2"></i>
												</div>
												<div v-else="bikeLock">
													Hide <i class="fa fa-chevron-up mx-2"></i>
												</div>
											</tw-button>
										</div>

									</div>
								</div>
							</div>
						</fieldset>
					</div>





					<div class="mt-4">
						<fieldset>
							<div>
								<div
									class="relative flex items-start bg-gray-50 border border-gray-200 px-2 pt-6 pb-3 rounded-md">
									<div class="flex items-center gap-4">
										<i class="far fa-camera text-3xl px-4 text-gray-700"></i>
									</div>
									<div class="ml-3 flex-col flex text-sm leading-5">
										<div class="mb-4">
											<h1 class="font-semibold text-lg">Photographic Evidence</h1>
										</div>
										<div class="mb-4">
											<span class="font-medium text-gray-700">
												Please note that it is a condition of this policy that you upload
												photographic evidence showing your bicycle(s) are in your possession and in
												good working order prior to any claim for theft, vandalism or accidental
												damage.
											</span>
										</div>


										<div v-if="showEvidenceInformation" class="text-gray-700 space-y-6">
											<div>
												<h4 class=" font-semibold text-gray-900">
													How do you do this?
												</h4>
												<p>
													This can be uploaded quickly and easily to your <a :href="$parent.setting.general.portal_url" target="blank">PMB Customer Dashboard</a>.
													Once
													you have completed the purchase of your policy, simply log into your PMB
													customer dashboard and follow the instructions to upload photographs of
													your
													bicycle(s).
												</p>
											</div>
											<div>
												<p>
													<strong class="font-semibold text-gray-900 ">Please Note.</strong> We will not cover
													any claim for theft, vandalism or accidental damage unless you have
													uploaded photographic evidence to the <a :href="$parent.setting.general.portal_url" target="blank">PMB Customer Dashboard</a>.
												</p>
											</div>
											<div>
												<p>
													You can also upload digital copies or photos of the payment receipts,
													invoices or any other documents that prove ownership of the bike,
													approved lock and accessories.
												</p>
											</div>
										</div>

										<div class="py-4">
											<tw-button variant="ghost bg-white" @click="showEvidenceInformation = !showEvidenceInformation">
												<div v-if="showEvidenceInformation == false">
													Find out more <i class="fa fa-chevron-right mx-2"></i>
												</div>
												<div v-else="showEvidenceInformation">
													Hide <i class="fa fa-chevron-up mx-2"></i>
												</div>
											</tw-button>
										</div>

									</div>
								</div>
							</div>
						</fieldset>
					</div>





				</div>
			</div>
			<loading :show="loading"></loading>

		</div>
	</div>
</template>

<script>
export default {
	name: 'quote.declaration',
	props: {
		loading: {},
		quote: {}
	},
	data() {
		return {
			termsAndConditions: false,
			bikeLock: false,
			showEvidenceInformation:false,
		}
	},
	computed: {
		isComplete() {
			return this.termsAndConditions;
		},
		currYear() {
			return '2021-22';
		}
	},
	watch: {
		termsAndConditions: function (newVal, oldVal) {
			this.$emit('refresh', { passed: this.isComplete });
		},
	},
	methods: {
		lockChange() {
			this.bikeLock = !this.bikeLock;
		}
	}
}
</script>
